import React, { useEffect, useMemo, useState } from "react";
import api, { errMsg } from "../../core/api";
import { useApp } from "../../core/AppContext";
import { fa, faMoney, formatDate, formatLongDate, todayIso } from "../../core/format";
import JalaliDate from "../../components/JalaliDate";
import Select from "../../components/Select";
import Table from "../../components/Table";

// کلید متادیتای فیلتر «وضعیت» برای هر گزارش
const STATUS_FILTER = {
  tasks: "taskStatus",
  requests: "requestStatus",
  repairs: "requestStatus",
  wear: "requestStatus",
  expenses: "requestStatus",
  equipment: "equipmentStatus",
  property: "equipmentStatus",
};

// فیلتر «نوع» (kind) برای هر گزارش
const KIND_FILTER = {
  pledges: { label: "نوع تعهدنامه", meta: "pledgeKinds" },
  incidents: { label: "نوع حادثه", meta: "incidentKinds" },
  requests: { label: "نوع درخواست", meta: "requestScopes" },
  letters: { label: "نوع نامه", meta: "letterDirections" },
  expenses: { label: "دسته‌بندی", meta: "expenseCategories" },
};

// گزارش‌هایی که فیلتر «کارمند» برایشان معنی ندارد
const NO_EMPLOYEE = [
  "expenses", "equipment", "property", "warehouse_good", "warehouse_worn",
  "repairs", "wear", "letters", "directives",
];

// ستون نوع شیفت همیشه «۲۴ ساعته» نمایش داده شود
const SHIFT_KEYS = ["shiftType", "shiftLabel", "shift"];
const rawCell = (c, r) => {
  if (SHIFT_KEYS.includes(c.key)) return "۲۴ ساعته";
  if (c.type === "date") return formatDate(r[c.key]);
  if (c.type === "money") return faMoney(r[c.key]);
  const v = r[c.key];
  return v === null || v === undefined || v === "" ? "—" : v;
};

const toOptions = (m) =>
  Array.isArray(m)
    ? m.map((v) => ({ value: v, label: v }))
    : Object.entries(m || {}).map(([value, label]) => ({ value, label }));

export default function Reports() {
  const { user, meta } = useApp();
  const [types, setTypes] = useState([]);
  const [emps, setEmps] = useState([]);
  const [org, setOrg] = useState(null);
  const [f, setF] = useState({ type: "", employeeId: "", from: "", to: "", status: "", kind: "" });
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const staff = user.role === "staff";

  useEffect(() => {
    api.get("/reports/types")
      .then((r) => { setTypes(r.data.items); setF((s) => ({ ...s, type: r.data.items[0]?.key || "" })); })
      .catch((e) => setErr(errMsg(e)));
    api.get("/org").then((r) => setOrg(r.data.org)).catch(() => {});
    if (!staff) api.get("/employees").then((r) => setEmps(r.data.items)).catch(() => {});
  }, [staff]);

  const run = async () => {
    if (f.from && f.to && f.to < f.from) return setErr("تاریخ پایان نباید قبل از تاریخ شروع باشد");
    setLoading(true); setErr("");
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([k, v]) => v && k !== "type"));
      const { data } = await api.get(`/reports/${f.type}`, { params });
      setRes({ ...data, filters: { ...f }, at: todayIso() });
    } catch (e) { setErr(errMsg(e)); } finally { setLoading(false); }
  };

  const csv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const cell = (c, r) =>
      SHIFT_KEYS.includes(c.key) ? "۲۴ ساعته" : c.type === "date" ? formatDate(r[c.key]) : r[c.key] ?? "";
    const lines = [
      ["ردیف", ...res.columns.map((c) => c.label)].map(esc).join(","),
      ...res.rows.map((r, i) => [i + 1, ...res.columns.map((c) => cell(c, r))].map(esc).join(",")),
    ];
    const blob = new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `report-${f.type}-${todayIso()}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  };

  const statusKey = STATUS_FILTER[f.type];
  const statusOpts = statusKey ? toOptions(meta?.[statusKey]) : [];
  const kindCfg = KIND_FILTER[f.type];
  const kindOpts = kindCfg ? toOptions(meta?.[kindCfg.meta]) : [];
  const showEmployee = !staff && !NO_EMPLOYEE.includes(f.type);
  const personName = (id) => {
    const e = emps.find((x) => String(x.id) === String(id));
    return e ? `${e.firstName} ${e.lastName}` : "";
  };

  const columns = useMemo(() => {
    if (!res) return [];
    return [
      { key: "_i", label: "ردیف", render: (r) => fa(res.rows.indexOf(r) + 1) },
      ...res.columns.map((c) => ({
        key: c.key,
        label: c.label,
        num: c.type === "money",
        render: (r) => rawCell(c, r),
      })),
    ];
  }, [res]);

  const resetFilters = () => setF((s) => ({ ...s, employeeId: "", from: "", to: "", status: "", kind: "" }));

  return (
    <>
      <div className="page-head no-print">
        <div>
          <h2>گزارش‌ها</h2>
          <p>{staff ? "گزارش فعالیت‌های خودتان" : "گزارش کامل بخش‌ها با فیلتر و امکان چاپ به‌صورت لیست"}</p>
        </div>
      </div>

      <div className="card card-pad no-print">
        {err && <div className="alert err">{err}</div>}
        <div className="grid2" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))" }}>
          <div className="field">
            <label>نوع گزارش</label>
            <Select
              value={f.type}
              searchable
              onChange={(v) => { setF({ ...f, type: v, status: "", kind: "", employeeId: "" }); setRes(null); }}
              options={types.map((t) => ({ value: t.key, label: t.title }))}
            />
          </div>
          {showEmployee && (
            <div className="field">
              <label>کارمند</label>
              <Select
                value={f.employeeId}
                onChange={(v) => setF({ ...f, employeeId: v })}
                searchable
                options={[
                  { value: "", label: "همه‌ی افراد" },
                  ...emps.map((e) => ({ value: e.id, label: `${e.firstName} ${e.lastName}` })),
                ]}
              />
            </div>
          )}
          {statusOpts.length > 0 && (
            <div className="field">
              <label>وضعیت</label>
              <Select value={f.status} onChange={(v) => setF({ ...f, status: v })} options={[{ value: "", label: "همه" }, ...statusOpts]} />
            </div>
          )}
          {kindOpts.length > 0 && (
            <div className="field">
              <label>{kindCfg.label}</label>
              <Select value={f.kind} onChange={(v) => setF({ ...f, kind: v })} options={[{ value: "", label: "همه" }, ...kindOpts]} />
            </div>
          )}
          <JalaliDate label="از تاریخ" value={f.from} onChange={(v) => setF((s) => ({ ...s, from: v }))} minYear={1400} maxYear={1420} />
          <JalaliDate label="تا تاریخ" value={f.to} onChange={(v) => setF((s) => ({ ...s, to: v }))} minYear={1400} maxYear={1420} />
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="btn" onClick={run} disabled={loading || !f.type}>
            {loading ? "در حال دریافت..." : "نمایش گزارش"}
          </button>
          <button className="btn btn-ghost" onClick={resetFilters}>پاک کردن فیلترها</button>
        </div>
      </div>

      {res && (
        <div className="card card-pad report-print" style={{ marginTop: 14 }}>
          <div className="print-head" style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 800, fontSize: 16, color: "var(--primary)" }}>
              {org?.orgName}{org?.unitName ? ` — ${org.unitName}` : ""}
            </div>
            <div style={{ fontWeight: 700, marginTop: 2 }}>{res.title}</div>
            <div className="hint">
              تاریخ تهیه: {formatLongDate(res.at)}
              {res.filters.employeeId ? ` · فرد: ${personName(res.filters.employeeId)}` : ""}
              {res.filters.from ? ` · از ${formatDate(res.filters.from)}` : ""}
              {res.filters.to ? ` تا ${formatDate(res.filters.to)}` : ""}
              {` · تعداد: ${fa(res.rows.length)} مورد`}
            </div>
          </div>

          <div className="no-print" style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "center", flexWrap: "wrap" }}>
            <span className="badge wait">{fa(res.rows.length)} مورد</span>
            {res.total !== null && res.total !== undefined && <span className="badge ok">جمع: {faMoney(res.total)} ریال</span>}
            <span style={{ flex: 1 }} />
            <button className="btn btn-sm btn-ghost" onClick={() => window.print()} disabled={!res.rows.length}>
              <i className="fa-solid fa-print" /> چاپ
            </button>
            <button className="btn btn-sm btn-ghost" onClick={csv} disabled={!res.rows.length}>
              <i className="fa-solid fa-file-csv" /> خروجی اکسل
            </button>
          </div>

          <Table rows={res.rows} empty="موردی با این فیلترها یافت نشد" columns={columns} />

          {res.total !== null && res.total !== undefined && res.rows.length > 0 && (
            <div style={{ textAlign: "left", fontWeight: 800, marginTop: 10 }}>جمع کل: {faMoney(res.total)} ریال</div>
          )}
          {org?.managerName && (
            <div className="print-only" style={{ marginTop: 40, textAlign: "left" }}>مسئول واحد: {org.managerName}</div>
          )}
        </div>
      )}
    </>
  );
}
